<template>
  <v-group
    name="grid-background-group"
    :listening="false"
  >
    <v-rect v-for="item in gridBase" :key="item.id" :config="item" />
    <v-line v-for="item in gridLines" :key="item.id" :config="item" />
    <v-line v-for="tick in xTicks" :key="tick.id" :config="tick" />
    <v-line v-for="tick in yTicks" :key="tick.id" :config="tick" />
    <v-text v-for="label in rulerLabels" :key="label.id" :config="label" />
  </v-group>
</template>

<script setup>
import { performanceAttrs } from './helpers'
// todo: add an interface, required option and remove this default config
const props = defineProps({
  config: {
    type: [Object, null],
    default: null,
  },
});

const config = computed(() => props.config);

const TICK_INTERVAL = config.value.footInPx / 4;

const rulerLabels = computed(() => {
  const labels = [];
  const base = {
    x: config.value.rulerSize / 2,
    y: config.value.rulerSize / 2,
    fontSize: 12,
    fill: config.value.rulerTextColor,
    align: "center",
  };

  for (let i = 0; i <= config.value.width; i += config.value.footInPx) {
    labels.push({
      ...base,
      id: `x-label-${i}`,
      name: `x-label-${i}`,
      x: config.value.rulerSize + i,
      text: ` ${i / config.value.footInPx}ft`,
    });
  }

  base.rotation = -90;

  for (
    let y = config.value.footInPx;
    y <= config.value.height;
    y += config.value.footInPx
  ) {
    labels.push({
      ...base,
      id: `y-label-${y}`,
      y: config.value.rulerSize + y,
      text: ` ${y / config.value.footInPx}ft`,
    });
  }

  return labels;
});

const xTicks = computed(() => {
  const ticks = [];

  for (let i = TICK_INTERVAL; i <= config.value.width; i += TICK_INTERVAL) {
    const isFoot = i % config.value.footInPx === 0;
    ticks.push({
      ...performanceAttrs,
      id: `x-tick-${i}`,
      points: [
        config.value.rulerSize + i,
        isFoot ? 0 : config.value.rulerSize / 2,
        config.value.rulerSize + i,
        config.value.rulerSize,
      ],
      stroke: config.value.rulerTextColor,
      strokeWidth: isFoot ? 1 : 0.5,
    });
  }
  return ticks;
});

const yTicks = computed(() => {
  const ticks = [];

  for (let i = TICK_INTERVAL; i <= config.value.height; i += TICK_INTERVAL) {
    const isFoot = i % config.value.footInPx === 0;
    ticks.push({
      ...performanceAttrs,
      id: `y-tick-${i}`,
      points: [
        isFoot ? 0 : config.value.rulerSize / 2,
        config.value.rulerSize + i,
        config.value.rulerSize,
        config.value.rulerSize + i,
      ],
      stroke: config.value.rulerTextColor,
      strokeWidth: isFoot ? 1 : 0.5,
    });
  }
  return ticks;
});

const gridBase = computed(() => [
  {
    name: "gridBackground",
    x: config.value.rulerSize,
    y: config.value.rulerSize,
    width: config.value.width,
    height: config.value.height,
    radius: 0,
    fill: config.value.bgColor,
    stroke: null,
    strokeWidth: 0,
    draggable: false,
  },
  {
    name: "xRulerBlock",
    x: config.value.rulerSize,
    y: 0,
    width: config.value.width,
    height: config.value.rulerSize,
    fill: "#ccc",
  },
  {
    name: "yRulerBlock",
    x: 0,
    y: config.value.rulerSize,
    width: config.value.rulerSize,
    height: config.value.height,
    fill: "#ccc",
  },
]);

const gridLines = computed(() => {
  const grid = []
  const padding = config.value.padding.x

  for (let i = 0; i < config.value.width / padding; i++) {
    grid.push({
      ...performanceAttrs,
      points: [
        config.value.rulerSize + Math.round(i * padding) + 0.5,
        config.value.rulerSize,
        config.value.rulerSize + Math.round(i * padding) + 0.5,
        config.value.rulerSize + config.value.height,
      ],
      stroke: "#ddd",
      strokeWidth: 1,
    });
  }

  for (let j = 0; j < config.value.height / padding; j++) {
    grid.push({
      ...performanceAttrs,
      points: [
        config.value.rulerSize,
        config.value.rulerSize + Math.round(j * padding),
        config.value.rulerSize + config.value.width,
        config.value.rulerSize + Math.round(j * padding),
      ],
      stroke: "#ddd",
      strokeWidth: 0.5,
    });
  }

  return grid;
});

const emit = defineEmits(['patternUpdated'])

onMounted(() => {
  console.log(`[3D Grid Pattern Mounted] with ${gridLines.value.length} lines`)
  emit('patternUpdated')
})
</script>
